import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, LockReset, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getAllUsers,
  createUser,
  updateUser,
  changeUserPassword,
  deleteUser
} from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
import { formatDateTime } from '../utils/dates';

const BTN_COLOR = '#db342c';
const BTN_HOVER = '#b82a23';
const EDIT_BTN_COLOR = '#00897b';
const DELETE_BTN_COLOR = '#c62828';

type Role = 'admin' | 'store_manager';

const roleLabel: Record<Role, string> = {
  admin: 'Admin',
  store_manager: 'Store Manager'
};

export const Users: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [active, setActiveUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    email: '',
    displayName: '',
    password: '',
    role: 'store_manager' as Role,
    isActive: true
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      setUsers(await getAllUsers());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setForm({ email: '', displayName: '', password: '', role: 'store_manager', isActive: true });
    setShowPassword(false);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await createUser(form.email.trim(), form.password, form.displayName.trim(), form.role);
      setCreateOpen(false);
      await loadUsers();
    } catch (e: unknown) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (u: User) => {
    setActiveUser(u);
    setForm({
      email: u.email,
      displayName: u.displayName,
      password: '',
      role: u.role,
      isActive: u.isActive
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await updateUser(active.uid, {
        displayName: form.displayName.trim(),
        role: form.role,
        isActive: form.isActive
      });
      setEditOpen(false);
      setActiveUser(null);
      await loadUsers();
    } catch (e: unknown) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u: User) => {
    try {
      await updateUser(u.uid, { isActive: !u.isActive });
      await loadUsers();
    } catch (e: unknown) {
      alert((e as Error).message);
    }
  };

  const handleOpenPassword = (u: User) => {
    setActiveUser(u);
    setForm((f) => ({ ...f, password: '' }));
    setShowPassword(false);
    setPasswordOpen(true);
  };

  const handleChangePassword = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await changeUserPassword(active.uid, form.password);
      setPasswordOpen(false);
      setActiveUser(null);
    } catch (e: unknown) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (u: User) => {
    setActiveUser(u);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await deleteUser(active.uid);
      setDeleteOpen(false);
      setActiveUser(null);
      await loadUsers();
    } catch (e: unknown) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const isSelf = (u: User) => currentUser?.uid === u.uid;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">User Management</Typography>
        <Box>
          <Button onClick={() => navigate('/dashboard')} size="small" sx={{ mr: 1, color: BTN_COLOR }}>
            ← Dashboard
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER } }}
          >
            Create user
          </Button>
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.uid}>
                    <TableCell>
                      <strong>{u.displayName}</strong>
                      {isSelf(u) && (
                        <Chip label="You" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                      )}
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={roleLabel[u.role]}
                        size="small"
                        color={u.role === 'admin' ? 'error' : 'default'}
                        variant={u.role === 'admin' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={u.isActive ? 'success' : 'default'}
                        variant={u.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{u.lastLogin ? formatDateTime(u.lastLogin) : '–'}</TableCell>
                    <TableCell>{formatDateTime(u.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(u)}
                          sx={{ color: EDIT_BTN_COLOR }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset password">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenPassword(u)}
                          sx={{ color: '#5f5f5f' }}
                        >
                          <LockReset fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isSelf(u) ? 'You cannot change your own status' : u.isActive ? 'Deactivate' : 'Activate'}>
                        <span>
                          <Switch
                            size="small"
                            checked={u.isActive}
                            disabled={isSelf(u)}
                            onChange={() => handleToggleActive(u)}
                          />
                        </span>
                      </Tooltip>
                      <Tooltip title={isSelf(u) ? 'You cannot delete your own account' : 'Delete'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDelete(u)}
                            disabled={isSelf(u)}
                            sx={{ color: DELETE_BTN_COLOR }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create user</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Display name"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            margin="normal"
            helperText="Minimum 8 characters"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            select
            fullWidth
            label="Role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
            margin="normal"
          >
            <MenuItem value="store_manager">Store Manager</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: BTN_COLOR }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={
              saving ||
              !form.displayName.trim() ||
              !form.email.trim() ||
              form.password.length < 8
            }
            sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER } }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {active?.email}
          </Typography>
          <TextField
            fullWidth
            label="Display name"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            select
            fullWidth
            label="Role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
            margin="normal"
            disabled={active ? isSelf(active) : false}
            helperText={active && isSelf(active) ? 'You cannot change your own role' : ''}
          >
            <MenuItem value="store_manager">Store Manager</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                disabled={active ? isSelf(active) : false}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} sx={{ color: BTN_COLOR }}>
            Cancel
          </Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={saving || !form.displayName.trim()}
            sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER } }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Set a new password for {active?.displayName} ({active?.email}).
          </Typography>
          <TextField
            fullWidth
            label="New password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            margin="normal"
            helperText="Minimum 8 characters"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordOpen(false)} sx={{ color: BTN_COLOR }}>
            Cancel
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={saving || form.password.length < 8}
            sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER } }}
          >
            Update password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          {active && (
            <Typography>
              Delete &quot;{active.displayName}&quot; ({active.email})? This cannot be undone. Users
              with existing stock records cannot be deleted — deactivate them instead.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} sx={{ color: BTN_COLOR }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: DELETE_BTN_COLOR, '&:hover': { bgcolor: '#a31f1f' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
